import { MessageItem } from "./MessageItem";
import { LoadingIndicator } from "./LoadingIndicator";

interface Message {
  role: "assistant" | "user";
  content: string | { content: string } | { response: string };
  id?: string;
}

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  canAskQuestion: boolean;
  hasInteracted?: boolean;
}

export const MessageList = ({ messages, isLoading, canAskQuestion, hasInteracted = false }: MessageListProps) => {
  // Helper function to extract content from message
  const extractContent = (message: Message): string => {
    if (typeof message.content === 'string') {
      return message.content;
    }
    
    if (typeof message.content === 'object' && message.content !== null) {
      if ('content' in message.content) {
        return message.content.content;
      }
      if ('response' in message.content) {
        return message.content.response;
      }
    }
    
    return String(message.content);
  };

  // Helper function to find the most recent user question for an assistant message
  const findUserQuestionForAssistant = (assistantIndex: number): string => {
    // Look backwards from the assistant message to find the most recent user message
    for (let i = assistantIndex - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        return extractContent(messages[i]);
      }
    }
    return '';
  };

  // Check if the last message is from assistant and has empty content
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const isLastMessageEmptyAssistant = lastMessage?.role === 'assistant' && 
                                     extractContent(lastMessage).trim() === '';
  
  return (
    <div className="flex flex-col w-full">
      {messages.map((message, index) => {
        const isLastAssistantMessage = index === messages.length - 1 && message.role === 'assistant';
        const content = extractContent(message);
        const isInitialGreeting = index === 0 && message.role === 'assistant';
        const isLastMessage = index === messages.length - 1;
        
        // Hide the initial greeting message if there are more than 2 messages (conversation has started)
        // but only for the first assistant message
        if (isInitialGreeting && messages.length > 2) {
          return null;
        }

        // For assistant messages, find the corresponding user question
        const userQuestion = message.role === 'assistant' ? findUserQuestionForAssistant(index) : '';
        
        return (
          <MessageItem 
            key={`message-${index}-${message.id || index}`}
            role={message.role} 
            content={
              // If this is the last assistant message and it's empty and we're loading,
              // render the loading indicator instead of empty content
              isLastAssistantMessage && content.trim() === '' && isLoading 
                ? <LoadingIndicator /> 
                : content
            }
            userQuestion={userQuestion}
          />
        );
      })}
      
      {/* Only show standalone loading indicator if there are no messages yet */}
      {isLoading && messages.length === 0 && (
        <div className="w-full py-5 flex bg-[#0c1221]/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-4 md:px-6 w-full flex">
            <div className="shrink-0 flex items-start mr-3 text-blue-500">
              <div className="h-7 w-7 md:h-8 md:w-8 rounded-full flex items-center justify-center overflow-hidden border border-blue-500/40 shadow-sm shadow-blue-500/20">
                <img 
                  src="/strykr-logo.jpeg" 
                  alt="Strykr AI" 
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="flex-1">
              <LoadingIndicator />
            </div>
          </div>
        </div>
      )}
      
      {!canAskQuestion && !isLoading && (
        <div className="w-full py-4 flex">
          <div className="max-w-4xl mx-auto px-4 md:px-6 w-full">
            <div className="bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-lg p-4 text-sm shadow-sm">
              Please wait for the response before asking another question.
            </div>
          </div>
        </div>
      )}
      
      {/* Empty state when no messages and not loading */}
      {messages.length === 0 && !isLoading && hasInteracted === false && (
        <div className="w-full py-12 flex items-center justify-center">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 pt-16 pb-6 flex flex-col items-center">
            {/* Centered branding */}
            <div className="flex flex-col items-center justify-center mb-8">
              <div className="h-16 w-16 rounded-full p-[2px] bg-gradient-to-r from-indigo-500 to-blue-500 flex items-center justify-center shadow-md shadow-indigo-500/20">
                <img 
                  src="/strykr-logo.jpeg" 
                  alt="Strykr AI" 
                  className="h-full w-full rounded-full" 
                />
              </div>
              <h1 className="text-2xl font-semibold text-white mt-3">Strykr AI</h1>
              <p className="text-gray-300 text-sm mt-2">Your financial market analysis assistant</p>
            </div>
            
            {/* Suggested questions header */}
            <div className="text-center w-full mb-4">
              <p className="text-gray-400 text-sm">Suggested questions</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Extra padding at the bottom to prevent content from being cut off */}
      {messages.length > 0 && <div className="h-12"></div>}
    </div>
  );
};
